import { FileText, TrendingUp, TrendingDown, Minus } from 'lucide-react'
import { Link } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { FluentCard } from '@/components/layout/page-header'
import type { TopTemplateAnalytics } from '@/lib/api/types'

interface TopTemplatesTableProps {
  data: TopTemplateAnalytics[]
  loading?: boolean
  title?: string
  description?: string
}

export function TopTemplatesTable({
  data,
  loading,
  title = 'Top Templates',
  description = 'Most used templates in the selected period',
}: TopTemplatesTableProps) {
  if (loading) {
    return (
      <FluentCard>
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2.5 rounded-xl bg-gradient-to-br from-violet-400 to-purple-600">
            <FileText className="h-5 w-5 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{title}</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">{description}</p>
          </div>
        </div>
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 dark:bg-gray-800/50 animate-pulse">
              <div className="w-10 h-10 rounded-lg bg-gray-200 dark:bg-gray-700" />
              <div className="flex-1">
                <div className="h-4 w-32 bg-gray-200 dark:bg-gray-700 rounded mb-2" />
                <div className="h-3 w-20 bg-gray-200 dark:bg-gray-700 rounded" />
              </div>
              <div className="h-6 w-16 bg-gray-200 dark:bg-gray-700 rounded-full" />
            </div>
          ))}
        </div>
      </FluentCard>
    )
  }

  // Safely calculate max count with fallback for undefined values
  const maxCount = Math.max(...data.map(t => t.count ?? 0), 1)

  return (
    <FluentCard>
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2.5 rounded-xl bg-gradient-to-br from-violet-400 to-purple-600">
          <FileText className="h-5 w-5 text-white" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{title}</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">{description}</p>
        </div>
      </div>

      {data.length > 0 ? (
        <div className="space-y-3">
          {data.map((template, index) => (
            <Link
              key={template.templateId}
              to={`/templates/${template.templateId}/analytics`}
              className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 dark:bg-gray-800/50 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors group"
            >
              {/* Rank */}
              <div className={cn(
                'w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold',
                index === 0 && 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
                index === 1 && 'bg-gray-200 text-gray-600 dark:bg-gray-600 dark:text-gray-300',
                index === 2 && 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
                index > 2 && 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
              )}>
                {index + 1}
              </div>

              {/* Thumbnail */}
              {template.templateThumbnail ? (
                <img
                  src={template.templateThumbnail}
                  alt={template.templateName}
                  className="w-10 h-10 rounded-lg object-cover"
                />
              ) : (
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center">
                  <FileText className="h-5 w-5 text-white" />
                </div>
              )}

              {/* Info */}
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-900 dark:text-white truncate group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                  {template.templateName}
                </p>
                <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                  <span>{(template.count ?? 0).toLocaleString()} uses</span>
                  {/* Progress bar */}
                  <div className="flex-1 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden max-w-[100px]">
                    <div
                      className="h-full bg-gradient-to-r from-violet-500 to-purple-500 rounded-full transition-all"
                      style={{ width: `${((template.count ?? 0) / maxCount) * 100}%` }}
                    />
                  </div>
                </div>
              </div>

              {/* Trend */}
              <div className={cn(
                'flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium',
                (template.trend ?? 0) > 0 && 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
                (template.trend ?? 0) < 0 && 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
                (template.trend ?? 0) === 0 && 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
              )}>
                {(template.trend ?? 0) > 0 && <TrendingUp className="h-3 w-3" />}
                {(template.trend ?? 0) < 0 && <TrendingDown className="h-3 w-3" />}
                {(template.trend ?? 0) === 0 && <Minus className="h-3 w-3" />}
                {(template.trend ?? 0) > 0 ? '+' : ''}{(template.trend ?? 0).toFixed(1)}%
              </div>
            </Link>
          ))}
        </div>
      ) : (
        <div className="text-center py-8">
          <FileText className="h-10 w-10 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
          <p className="text-sm text-gray-500 dark:text-gray-400">No template data available</p>
        </div>
      )}
    </FluentCard>
  )
}
